import CalendarDateModel from "./CalendarDateModel.js";
import CalendarConfigModel from "./CalendarConfigModel.js";
export default class SelectedDatesModel
{
    /**
     * @param {CalendarConfigModel} calendarConfigModel
     * @param {string} uuid
     * @param {string|null} hotelID
     */
    constructor(calendarConfigModel, uuid, hotelID = null)
    {
        this.calendarDateModels = [];
        this.calendarConfigModel = calendarConfigModel;
        this.uuid = uuid;
        this.hotelID = hotelID;
    }
    /**
     * @param calendarDateModels {Map<CalendarDateModel>}
     */
    setCalendarDateModels(calendarDateModels)
    {
        calendarDateModels.forEach(calendarDateModel => {
            calendarDateModel.isSelected = true;
        });

        this.calendarDateModels = calendarDateModels;
    }

    clearSelectedDates()
    {
        CalendarDateModel.clearSelectedDates(this.calendarDateModels);

        this.calendarDateModels = [];
    }

    /**
     * @param calendarDateModel {CalendarDateModel}
     * @return {boolean}
     */
    isAdditionalDateSelectableForArrival(calendarDateModel)
    {
        if (this.isEmpty()) {
            return true;
        }

        const departureCalendarDateModel = this.getDepartureCalendarDateModel();

        return (

            calendarDateModel.getDate() < this.getArrivalCalendarDateModel().getDate() ||
            departureCalendarDateModel.isSameDate(calendarDateModel)
        );
    }

    /**
     * @param calendarDateModel {CalendarDateModel}
     * @returns {boolean}
     */
    isCalendarDateModelSelectableForAdditionalDepartureDate(calendarDateModel)
    {
        if (! this.hasSelectedDates()) {
            return false;
        }

        return calendarDateModel.isCalendarDateAfterDate(
            this.getDepartureCalendarDateModel()
        );
    }

    /**
     * @param calendarDateModel {CalendarDateModel}
     * @returns {string[]}
     */
    getDatesBetweenSelectedDepartureDateAndCalendarDate(calendarDateModel)
    {
        return CalendarDateModel.getRangeDates(
            this.getDepartureCalendarDateModel(),
            calendarDateModel
        );
    }

    /**
     * @param calendarDateModel {CalendarDateModel}
     * @returns {boolean}
     */
    isCalendarDateModelSelectableForAdditionalArrivalDate(calendarDateModel)
    {
        if (! this.hasSelectedDates()) {
            return false;
        }

        return calendarDateModel.isCalendarDateBeforeDate(
            this.getArrivalCalendarDateModel()
        );
    }

    /**
     * @param calendarDateModel {CalendarDateModel}
     * @returns {string[]}
     */
    getDatesBetweenSelectedArrivalDateAndCalendarDate(calendarDateModel)
    {
        return CalendarDateModel.getRangeDates(
            calendarDateModel,
            this.getArrivalCalendarDateModel()
        );
    }


    /**
     * @param selectedArrivalCalendarDateModel {CalendarDateModel}
     * @param calendarDateModel {CalendarDateModel}
     * @returns {boolean}
     */
    isAdditionalDateSelectableForDeparture(selectedArrivalCalendarDateModel, calendarDateModel)
    {
        if (this.isEmpty()) {
            return true;
        }

        const departureCalendarDateModel = this.getDepartureCalendarDateModel();
        const arrivalCalendarDateModel = this.getArrivalCalendarDateModel();

        if (selectedArrivalCalendarDateModel.getDate() < arrivalCalendarDateModel.getDate()) {
            return arrivalCalendarDateModel.isSameDate(calendarDateModel);
        }

        return calendarDateModel.getDate() > departureCalendarDateModel.getDate();
    }

    /**
     * @returns {string|null}
     */
    getHotelID()
    {
        return this.hotelID;
    }

    /**
     * @returns {string}
     */
    getUUID()
    {
        return this.uuid;
    }

    /**
     * @param {string} date
     * @returns {boolean}
     */
    hasDate(date)
    {
        let hasDate = false;
        for (const calendarDateModel of this.calendarDateModels)
        {
            if (calendarDateModel.getDate() == date) {
                hasDate = true;
                break;
            }
        }

        return hasDate;
    }

    /**
     * @return {number}
     */
    getNumberOfNights()
    {
        return this.calendarDateModels.length > 0 ? this.calendarDateModels.length - 1 : 0;
    }

    /**
     * @return {boolean}
     */
    hasSelectedDates()
    {
        return this.calendarDateModels.length > 1;
    }

    /**
     * @param calendarDateModel {CalendarDateModel}
     * @return {boolean}
     */
    isDateBeforeCurrentSelectedArrivalDate(calendarDateModel)
    {
        return (
            this.hasOnlySelectedArrivalDate() &&
            calendarDateModel.spacetimeDate.isBefore(this.getArrivalCalendarDateModel().spacetimeDate)
        );
    }

    /**
     * @returns {boolean}
     */
    hasOnlySelectedArrivalDate()
    {
        return this.calendarDateModels.length == 1;
    }

    /**
     * @return {boolean}
     */
    hasSelectedArrival()
    {
        return this.calendarDateModels.length > 0;
    }

    /**
     * @returns {boolean}
     */
    isEmpty()
    {
        return this.calendarDateModels.length == 0;
    }

    /**
     * @param date {string}
     * @returns {boolean}
     */
    isDateArrivalDate(date)
    {
        return this.getArrivalDate() == date;
    }

    /**
     * @param date {string}
     * @returns {boolean}
     */
    isDateDepartureDate(date)
    {
        return this.getDepartureDate() == date;
    }

    /**
     * @param {string} format
     * @return {string|null}
     */
    getArrivalDate(format = CalendarDateModel.DATE_FORMAT)
    {
        let arrivalDate = null;
        if (this.hasSelectedArrival()) {
            arrivalDate = this.getArrivalCalendarDateModel().getDateInFormat(format);
        }
        return arrivalDate;
    }

    /**
     * @returns {CalendarDateModel|null}
     */
    getArrivalCalendarDateModel()
    {
        return this.calendarDateModels[0] ?? null;
    }

    /**
     * @returns {CalendarDateModel|null}
     */
    getDepartureCalendarDateModel()
    {
        return this.calendarDateModels[this.calendarDateModels.length - 1] ?? null;
    }

    /**
     * @param format {string}
     * @return {string|null}
     */
    getDepartureDate(format = CalendarDateModel.DATE_FORMAT)
    {
        let departureDate = null;
        if (this.hasSelectedDates()) {
            departureDate = this.getDepartureCalendarDateModel().getDateInFormat(format);
        }
        return departureDate;
    }

    /**
     * @returns {string[]}
     */
    getSelectedDates()
    {
        return this.calendarDateModels.map(calendarDateModel => calendarDateModel.getDate());
    }

    /**
     * @return {number}
     */
    getTotalPrice()
    {
        return this.calendarDateModels.reduce((amount, calendarDate) => {
            amount += calendarDate.price;
            return amount;
        }, 0);
    }
    /**
     * @return {string}
     */
    getTotalPriceWithCurrency()
    {
        return this.getCurrency() + this.getTotalPrice().toFixed(this.calendarConfigModel.decimalsPlaces);
    }
    /**
     * @param segment {number}
     * @return {string}
     */
    getPriceSegment(segment)
    {
        return this.getTotalPrice().toFixed(2).split('.')[segment];
    }
    /**
     * @return {string}
     */
    getPriceUnit()
    {
        return this.getPriceSegment(0);
    }
    /**
     * @return {string}
     */
    getPriceCents()
    {
        return this.getPriceSegment(1);
    }
    /**
     * @return {string}
     */
    getCurrency()
    {
        return this.calendarConfigModel.currency;
    }
    /**
     * @return {string|null}
     */
    getRatesRoomsUrl()
    {
        let link = null;
        if (this.hasSelectedDates()) {
            const arrivalCalendarDateModel = this.getArrivalCalendarDateModel();
            const url = new URL(arrivalCalendarDateModel.getLink());
            url.searchParams.set('departure', this.getDepartureDate(CalendarDateModel.DATE_FORMAT));
            url.searchParams.set('nights', this.getNumberOfNights());
            link = url.href;
        }
        return link;
    }
}
